"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { Container } from "@/components/layout/container";
import { SceneAtmosphere } from "@/components/scroll/scene-atmosphere";
import { KineticHeadline } from "@/components/ui/kinetic-headline";
import { KpiCounter } from "@/components/ui/kpi-counter";
import { PipelineDiagram } from "@/components/ui/pipeline-diagram";
import { COMPANY_FACTS, PROCESS_STEPS, VALUES } from "@/lib/about";

if (typeof window !== "undefined") gsap.registerPlugin(ScrollTrigger);

const INDUSTRIES = [
  {
    industry: "Fintech",
    project: "UniLabs",
    detail:
      "An AI-powered DeFi asset management platform built on Ethereum and Solidity, democratizing access to advanced crypto investment strategies.",
    href: "/portfolio/unilabs",
  },
  {
    industry: "Education",
    project: "ThinkFirst",
    detail:
      "An EdTech web application that uses AI-generated content to transform grammar learning in schools.",
    href: "/portfolio/thinkfirst",
  },
  {
    industry: "E-Commerce",
    project: "Senzi",
    detail:
      "A dropshipping platform for the Libyan market, importing products directly from 1688 and Taobao.",
    href: "/portfolio",
  },
  {
    industry: "Retail",
    project: "Provision Planner",
    detail:
      "A cross-platform desktop application for managing household provisions, built with Electron and React.",
    href: "/portfolio/provision-planner",
  },
  {
    industry: "Healthcare",
    project: "Compliance-first builds",
    detail:
      "Secure, user-centric applications that improve patient outcomes and streamline clinical workflows.",
    href: "/services",
  },
] as const;

export function AboutContent() {
  const spineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = spineRef.current;
    if (!root) return;
    const fill = root.querySelector<HTMLElement>("[data-spine-fill]");
    const steps = root.querySelectorAll<HTMLElement>("[data-step]");
    if (!fill) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      gsap.set(fill, { scaleY: 1 });
      gsap.set(steps, { opacity: 1, y: 0 });
      return;
    }

    const ctx = gsap.context(() => {
      gsap.fromTo(
        fill,
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: "none",
          scrollTrigger: { trigger: root, start: "top 70%", end: "bottom 70%", scrub: 0.5 },
        }
      );
      steps.forEach((step) => {
        gsap.fromTo(
          step,
          { opacity: 0, y: 24 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: "power3.out",
            scrollTrigger: { trigger: step, start: "top 80%", toggleActions: "play none none reverse" },
          }
        );
      });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <div className="bg-[var(--color-bg)]">
      {/* Lede: one wide statement, no columns — the definitional answer sits alone so it reads as
          the page's thesis and lifts cleanly into an AI-generated answer. */}
      <section
        aria-labelledby="about-summary-heading"
        className="relative overflow-hidden px-6 py-12 sm:py-[9vh] lg:px-10"
      >
        <SceneAtmosphere x="16%" y="24%" intensity={0.14} size={65} />
        <Container className="relative z-10">
          <KineticHeadline
            as="h2"
            id="about-summary-heading"
            variant="slide-up"
            lines={["Exec9 is a full-cycle", "software agency in Lahore."]}
            className="max-w-4xl font-display text-[clamp(1.9rem,4vw,3.4rem)] font-bold leading-[1.05] tracking-[-0.02em] text-[var(--color-text-primary)]"
          />
          <div className="mt-10 grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
            <p className="text-lg leading-relaxed text-pretty text-[var(--color-text-secondary)]">
              The team designs, builds, and ships AI-powered web, mobile, and blockchain products for
              clients worldwide — covering architecture, engineering, design, QA, and project
              management on one connected pipeline rather than handing work between separate vendors.
            </p>
            <p className="text-base leading-relaxed text-pretty text-[var(--color-text-tertiary)]">
              Over 10+ years Exec9 has delivered more than 250 projects across Retail, E-Commerce,
              Fintech, Healthcare, and Education, reaching over 400,000 people. Clients own their
              code and the outcome outright.
            </p>
          </div>
        </Container>
      </section>

      {/* Stats: animated counters, the same instrument-strip device the homepage uses. */}
      <section aria-label="Exec9 by the numbers" className="relative px-6 py-9 sm:py-[6vh] lg:px-10">
        <Container className="relative z-10">
          <div className="grid grid-cols-2 gap-8 border-y border-[var(--color-border)] py-12 sm:grid-cols-4">
            {COMPANY_FACTS.map((fact) => (
              <KpiCounter key={fact.label} finalValue={fact.value} label={fact.label} />
            ))}
          </div>
        </Container>
      </section>

      {/* Industries: an editorial list — industry, the project that proves it, the link. No cards. */}
      <section
        id="who-we-help"
        aria-labelledby="who-we-help-heading"
        className="relative overflow-hidden px-6 py-12 sm:py-[9vh] lg:px-10"
      >
        <SceneAtmosphere x="84%" y="34%" intensity={0.12} size={70} />
        <Container className="relative z-10">
          <span className="font-mono-tech text-[0.7rem] uppercase tracking-[0.15em] text-[var(--color-primary-text)]">
            Who we help
          </span>
          <KineticHeadline
            as="h2"
            id="who-we-help-heading"
            variant="slide-up"
            lines={["Founders and teams", "who ship."]}
            className="mt-4 max-w-3xl font-display text-[clamp(1.75rem,3.5vw,3rem)] font-bold leading-[1.05] tracking-[-0.02em] text-[var(--color-text-primary)]"
          />
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-pretty text-[var(--color-text-secondary)]">
            Exec9 works with founders validating a first product and with established companies
            scaling platforms already in production — across five industries.
          </p>

          <ul className="mt-14 flex flex-col">
            {INDUSTRIES.map((item) => (
              <li key={item.industry} className="border-t border-[var(--color-border)] last:border-b">
                <Link
                  href={item.href}
                  className="group grid items-baseline gap-x-8 gap-y-2 py-7 transition-colors md:grid-cols-[minmax(0,10rem)_minmax(0,12rem)_1fr]"
                >
                  <h3 className="font-display text-xl font-bold text-[var(--color-text-primary)] transition-colors group-hover:text-[var(--color-primary-text)]">
                    {item.industry}
                  </h3>
                  <span className="font-mono-tech text-[0.7rem] uppercase tracking-[0.15em] text-[var(--color-primary-text)]">
                    {item.project}
                  </span>
                  <p className="max-w-[60ch] text-[0.9375rem] leading-relaxed text-pretty text-[var(--color-text-secondary)]">
                    {item.detail}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      {/* Process: a scroll-drawn spine, echoing the pipeline device on /services. */}
      <section
        id="process"
        aria-labelledby="process-heading"
        className="relative overflow-hidden px-6 py-12 sm:py-[9vh] lg:px-10"
      >
        <SceneAtmosphere x="22%" y="60%" intensity={0.13} size={68} />
        <Container className="relative z-10">
          <span className="font-mono-tech text-[0.7rem] uppercase tracking-[0.15em] text-[var(--color-primary-text)]">
            Process
          </span>
          <KineticHeadline
            as="h2"
            id="process-heading"
            variant="slide-up"
            lines={["Four stages,", "one pipeline."]}
            className="mt-4 font-display text-[clamp(1.75rem,3.5vw,3rem)] font-bold leading-[1.05] tracking-[-0.02em] text-[var(--color-text-primary)]"
          />
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-pretty text-[var(--color-text-secondary)]">
            Scope and timeline are agreed in the first stage, before any code is written.
          </p>

          <div className="mt-16 grid gap-12 lg:grid-cols-[1fr_minmax(0,20rem)] lg:items-start lg:gap-20">
            <div ref={spineRef} className="relative pl-14 sm:pl-24">
            <div
              className="absolute left-[18px] top-2 bottom-2 w-px bg-[var(--color-border)] sm:left-[34px]"
              aria-hidden
            />
            <div
              data-spine-fill
              aria-hidden
              className="absolute left-[18px] top-2 bottom-2 w-px origin-top bg-gradient-to-b from-[var(--color-primary-light)] via-[var(--color-primary)] to-[var(--color-primary-dark)] sm:left-[34px]"
            />
            <ol className="flex flex-col gap-[6vh]">
              {PROCESS_STEPS.map((step, i) => (
                <li key={step.name} data-step className="relative">
                  <span
                    aria-hidden
                    className="absolute -left-14 top-0 flex h-9 w-9 items-center justify-center rounded-full border border-[var(--color-primary)]/40 bg-[var(--color-bg)] font-mono-tech text-[0.7rem] text-[var(--color-primary-text)] sm:-left-24 sm:h-10 sm:w-10"
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-2xl font-bold text-[var(--color-text-primary)]">
                    {step.name}
                  </h3>
                  <p className="mt-3 max-w-[62ch] text-base leading-relaxed text-pretty text-[var(--color-text-secondary)]">
                    {step.text}
                  </p>
                </li>
              ))}
            </ol>
          </div>

            <PipelineDiagram className="hidden aspect-[228/302] w-full lg:sticky lg:top-32 lg:block" />
          </div>
        </Container>
      </section>

      {/* Values: prose in two columns — deliberately the quietest section on the page. */}
      <section
        id="values"
        aria-labelledby="values-heading"
        className="relative overflow-hidden px-6 py-12 sm:py-[9vh] lg:px-10"
      >
        <SceneAtmosphere x="80%" y="42%" intensity={0.12} size={66} />
        <Container className="relative z-10">
          <span className="font-mono-tech text-[0.7rem] uppercase tracking-[0.15em] text-[var(--color-primary-text)]">
            Values
          </span>
          <KineticHeadline
            as="h2"
            id="values-heading"
            variant="slide-up"
            lines={["What Exec9 stands for."]}
            className="mt-4 font-display text-[clamp(1.75rem,3.5vw,3rem)] font-bold leading-[1.05] tracking-[-0.02em] text-[var(--color-text-primary)]"
          />
          <div className="mt-12 grid gap-x-16 gap-y-10 md:grid-cols-2">
            {VALUES.map((value) => (
              <div key={value.title}>
                <h3 className="font-display text-xl font-bold text-[var(--color-primary-text)]">
                  {value.title}
                </h3>
                <p className="mt-3 max-w-[58ch] text-base leading-relaxed text-pretty text-[var(--color-text-secondary)]">
                  {value.body}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </section>
    </div>
  );
}
